import { apiGet } from './client';

// ===== Types =====

export interface PropertyMetrics {
  propertyId: string;
  propertyName: string;
  sessions: number;
  totalUsers: number;
  newUsers: number;
  pageViews: number;
  avgSessionDuration: number;
  bounceRate: number;
}

export interface TrafficSource {
  source: string;
  medium: string;
  sessions: number;
  users: number;
}

export interface DailyMetrics {
  date: string;
  sessions: number;
  users: number;
  pageViews: number;
}

export interface TopPage {
  path: string;
  pageViews: number;
  avgTimeOnPage: number;
}

export interface ChannelBreakdown {
  channel: string;
  sessions: number;
  users: number;
}

export interface CountryBreakdown {
  country: string;
  sessions: number;
  users: number;
}

export interface DeviceBreakdown {
  device: string;
  sessions: number;
  users: number;
}

export interface EventBreakdown {
  eventName: string;
  count: number;
}

export interface PropertyAnalytics {
  metrics: PropertyMetrics;
  sources: TrafficSource[];
  channels: ChannelBreakdown[];
  countries: CountryBreakdown[];
  devices: DeviceBreakdown[];
  events: EventBreakdown[];
  daily: DailyMetrics[];
  topPages: TopPage[];
}

export interface DuplicateTagSite {
  hostname: string;
  productName: string;
  streamIds: string[];
}

export interface StreamWarning {
  hostname: string;
  productName: string;
  canonicalStreamId: string;
  observedStreamIds: string[];
}

// Always present per the backend guarantee from plan 85-05: `duplicateTagSites`
// and `streamWarnings` are both non-optional arrays (possibly empty, never
// undefined), so the frontend never needs an `?? []` guard before rendering.
export interface WebAnalyticsDiagnostics {
  duplicateTagSites: DuplicateTagSite[];
  streamWarnings: StreamWarning[];
}

export interface WebAnalyticsReport {
  properties: PropertyAnalytics[];
  totals: {
    sessions: number;
    totalUsers: number;
    newUsers: number;
    pageViews: number;
  };
  channels: ChannelBreakdown[];
  countries: CountryBreakdown[];
  devices: DeviceBreakdown[];
  events: EventBreakdown[];
  diagnostics: WebAnalyticsDiagnostics;
}

export interface GA4Property {
  name: string;
}

// ===== API =====

export interface WebAnalyticsFilters {
  from: string;
  to: string;
  products?: string[];
}

export async function getWebAnalyticsReport(
  filters: WebAnalyticsFilters
): Promise<WebAnalyticsReport> {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.products?.length) {
    params.set('products', filters.products.join(','));
  }
  const res = await apiGet<{ data: WebAnalyticsReport }>(
    `/web-analytics/report?${params.toString()}`
  );
  return res.data;
}

export async function getGA4Properties(): Promise<GA4Property[]> {
  const res = await apiGet<{ data: GA4Property[] }>('/web-analytics/properties');
  return res.data;
}

export interface EngagementBySource {
  product: string;
  sessions: number;
  bounceRate: number;
  avgSessionDuration: number;
  pageViews: number;
  pagesPerSession: number;
}

export interface MonthlyEngagement {
  month: string;
  product: string;
  sessions: number;
  bounceRate: number;
  avgSessionDuration: number;
  pagesPerSession: number;
}

export async function getEngagementTrend(
  months: number = 6
): Promise<MonthlyEngagement[]> {
  const params = new URLSearchParams({ months: String(months) });
  const res = await apiGet<{ data: MonthlyEngagement[] }>(
    `/web-analytics/engagement/trend?${params.toString()}`
  );
  return res.data;
}

export async function getEngagementBySource(
  from: string,
  to: string,
  source: string
): Promise<EngagementBySource[]> {
  const params = new URLSearchParams({ from, to, source });
  const res = await apiGet<{ data: EngagementBySource[] }>(
    `/web-analytics/engagement?${params.toString()}`
  );
  return res.data;
}
